import { LoadingOutlined } from '@ant-design/icons'
import { useTanks } from '@api/useTanks'
import { DataItem, ErrorNotification, GraviButton } from '@gravitate-js/excalibrr'
import api from '@utils/api'
import { Drawer, Form, InputNumber, Select } from 'antd'
import React, { useState } from 'react'

import { CustomSelect } from '../../../_SiteWide/Form/DropdownSelect'
import { FormInput } from '../../../_SiteWide/Form/FormInput'
import { ManifoldSelect } from './Detail/TankSizing/ManifoldSelect'

const { Option } = Select
export function CreateTankDrawer({ metadata, showingDrawer, setShowingDrawer, triggerRefresh }) {
  const { createTankMutation } = useTanks()
  const { mutateAsync } = createTankMutation()

  const [form] = Form.useForm()
  const [availableManifolds, setAvailableManifolds] = useState()
  const [loading, setLoading] = useState(false)

  const onClose = () => {
    form.resetFields()
    setShowingDrawer(false)
    setLoading(false)
  }
  const findManifolds = (storeId) => {
    const storeNumber = metadata.stores.find((store) => store.store_id === storeId)?.store_number
    api.getStoreManifolds(storeNumber).then((resp) =>
      setAvailableManifolds(
        resp.data &&
          Object.keys(resp.data).map((key) => ({
            manifold_id: key,
            tanks: resp.data[key],
          }))
      )
    )
  }
  const handleSave = (formData) => {
    setLoading(true)

    const { carrier } = formData
    if (carrier === undefined && formData.island === 'carrier') {
      formData.island = 'auto'
    } else if (carrier && formData.island !== 'carrier') {
      formData.island = 'carrier'
    }
    if (formData.carrier_id) {
      formData.carrier = metadata.carriers.find((carrier) => carrier.id === formData.carrier_id)?.name
    }
    formData.store_number = metadata.stores.find((store) => store.store_id === formData.store_id)?.store_number

    mutateAsync(formData)
      .then(() => {
        setShowingDrawer(false)
        triggerRefresh(formData)
        setLoading(false)
        form.resetFields()
      })
      .catch(() => {
        setLoading(false)
      })
  }
  if (!metadata) return null
  return (
    <Drawer title='Create Tank' placement='right' closable onClose={onClose} open={showingDrawer}>
      <div className='flex flex-column full-height-width'>
        <div className='flex-1'>
          <Form
            form={form}
            className='create-tank-form'
            size='small'
            onFinish={handleSave}
            onFinishFailed={(msg) => {
              setLoading(false)
              ErrorNotification(msg)
            }}
          >
            <DataItem label='Site Number'>
              <Form.Item name='store_id' rules={[{ required: true, message: 'Site Number is required' }]}>
                <Select
                  allowClear
                  placeholder='Search by Site Number'
                  message='Site Number is required'
                  showSearch
                  onSelect={(value) => findManifolds(value)}
                  filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                  style={{ width: '100%' }}
                >
                  {metadata.stores.map((store, i) => (
                    <Option key={i} value={store.store_id}>
                      {store.store_number}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </DataItem>
            <DataItem label='Tank Id'>
              <Form.Item name='tank_id' rules={[{ required: true, message: 'Tank Id is required' }]}>
                <InputNumber editable min={0} style={{ width: '100%' }} />
              </Form.Item>
            </DataItem>
            <DataItem label='Description'>
              <FormInput name='description' editable />
            </DataItem>
            <DataItem label='Product'>
              <Form.Item name='product' rules={[{ required: true, message: 'Product is required' }]}>
                <Select
                  allowClear
                  placeholder='Search by Product'
                  showSearch
                  filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                  style={{ width: '100%' }}
                >
                  {metadata.products.map((product, i) => (
                    <Option key={i} value={product.name}>
                      {product.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </DataItem>

            <DataItem label='Tank Manifold'>
              <ManifoldSelect name='manifold_id' availableManifolds={availableManifolds} />
            </DataItem>

            <DataItem label='Island'>
              <FormInput name='island' editable required={false} />
            </DataItem>
            <DataItem label='Carrier'>
              <Form.Item name='carrier_id'>
                <Select
                  allowClear
                  placeholder='Search by Carrier'
                  showSearch
                  style={{ width: '100%' }}
                  filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                >
                  {metadata.carriers.map((carrier, i) => (
                    <Option key={i} value={carrier.id}>
                      {carrier.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </DataItem>
            <DataItem label='Tank Size'>
              <Form.Item name='tank_size' rules={[{ required: true, message: 'Tank Size is required' }]}>
                <InputNumber style={{ width: '100%' }} editable min={0} />
              </Form.Item>
            </DataItem>
            <DataItem label='Storage Max'>
              <Form.Item name='storage_max' rules={[{ required: true, message: 'Storage Max is required' }]}>
                <InputNumber style={{ width: '100%' }} editable min={0} />
              </Form.Item>
            </DataItem>
            <DataItem label='Fuel Bottom'>
              <Form.Item name='fuel_bottom' rules={[{ required: true, message: 'Fuel Bottom is required' }]}>
                <InputNumber style={{ width: '100%' }} editable min={0} />
              </Form.Item>
            </DataItem>
            <DataItem label='Manufacturer'>
              <FormInput name='manufacturer' editable />
            </DataItem>
            <DataItem label='Dimensions'>
              <FormInput name='dimensions' editable />
            </DataItem>
            <DataItem label='Tank Color'>
              <FormInput name='tank_color' editable />
            </DataItem>
            <DataItem label='Tank Chart'>
              <Form.Item name='tank_chart'>
                <Select
                  allowClear
                  name='tank_chart'
                  placeholder='Search by Tank Chart'
                  showSearch
                  style={{ width: '100%' }}
                >
                  {metadata?.tank_charts?.map((chart, i) => (
                    <Option key={i} value={chart}>
                      {chart}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </DataItem>
            <DataItem label='Tank Tags'>
              <CustomSelect
                options={metadata.tag_options || []}
                width='100%'
                editable
                placeholder='Add New Option'
                name='filter_tags'
              />
            </DataItem>
          </Form>
        </div>
        <div className='flex items-end justify-end flex-1' data-cy='create-tank-btn'>
          <GraviButton
            theme2
            buttonText='Create Tank'
            disabled={loading}
            icon={loading && <LoadingOutlined spin />}
            onClick={() => form.submit()}
          >
            Create Tank
          </GraviButton>
        </div>
      </div>
    </Drawer>
  )
}
